import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { config } from '../config';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import { FaChalkboardTeacher, FaLinkedin, FaGithub, FaTwitter } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';

const apiUrl = config.apiUrl;
const imageUrl = config.imageUrl;

const Instructors = () => {
  const [instructors, setInstructors] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeInstructor, setActiveInstructor] = useState(null);

  useEffect(() => {
    axios.get(`${apiUrl}/instructors`)
      .then((response) => {
        setInstructors(response.data.instructors || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setError('Failed to load instructors data');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-md max-w-md">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Error Loading Instructors</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      
      <main className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white overflow-hidden pt-36">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-300 rounded-full mix-blend-screen filter blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300 rounded-full mix-blend-screen filter blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <FaChalkboardTeacher className="mr-2" />
                <span className="font-medium">MEET THE TEAM</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">আমাদের বিশেষজ্ঞ প্রশিক্ষকবৃন্দ</h1>
              <p className="text-xl md:text-2xl max-w-3xl mx-auto">
                অভিজ্ঞতা ও নিষ্ঠার সমন্বয়ে গড়ে উঠেছে আমাদের দক্ষ প্রশিক্ষক টিম
              </p>
            </motion.div>
          </div>
        </section>

        {/* Instructors Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {instructors.map((instructor, index) => (
                <motion.div
                  key={instructor.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden group"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={`${imageUrl}/${instructor.image}`}
                      alt={instructor.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  </div>
                  
                  <div className="p-6 text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{instructor.name}</h2>
                    <h3 className="text-lg text-blue-600 font-medium mb-4">{instructor.designation}</h3>
                    <p className="text-gray-600 mb-6">{instructor.desc}</p>
                    
                    <div className="flex justify-center space-x-4">
                      {instructor.linkedin && (
                        <a href={instructor.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-700">
                          <FaLinkedin className="text-xl" />
                        </a>
                      )}
                      {instructor.github && (
                        <a href={instructor.github} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                          <FaGithub className="text-xl" />
                        </a>
                      )}
                      {instructor.twitter && (
                        <a href={instructor.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-400">
                          <FaTwitter className="text-xl" />
                        </a>
                      )}
                      {instructor.email && (
                        <a href={`mailto:${instructor.email}`} className="text-gray-500 hover:text-red-500">
                          <MdEmail className="text-xl" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Instructor Details Modal */}
        {activeInstructor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="relative">
                <div className="h-64 w-full bg-gray-200 overflow-hidden">
                  <img
                    src={`${imageUrl}/${activeInstructor.image}`}
                    alt={activeInstructor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button 
                  onClick={() => setActiveInstructor(null)}
                  className="absolute top-4 right-4 bg-white p-2 rounded-full shadow-md hover:bg-gray-100"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{activeInstructor.name}</h2>
                <h3 className="text-xl text-blue-600 font-medium mb-6">{activeInstructor.designation}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3">Expertise</h4>
                    <ul className="space-y-2">
                      {activeInstructor.expertise?.split(',').map((skill, i) => (
                        <li key={i} className="flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          {skill.trim()}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3">Experience</h4>
                    <p>{activeInstructor.experience || '5+ years'}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3">Contact</h4>
                    <div className="flex space-x-4">
                      {activeInstructor.linkedin && (
                        <a href={activeInstructor.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-700">
                          <FaLinkedin className="text-xl" />
                        </a>
                      )}
                      {activeInstructor.email && (
                        <a href={`mailto:${activeInstructor.email}`} className="text-gray-500 hover:text-red-500">
                          <MdEmail className="text-xl" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="prose max-w-none">
                  <h4 className="font-bold text-gray-700 mb-3">About</h4>
                  <p className="text-gray-600">{activeInstructor.desc}</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Join Team CTA */}
        <section className="py-16 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-center">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">আমাদের টিমে যোগ দিতে চান?</h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto">
                আপনি যদি একজন দক্ষ প্রশিক্ষক হন এবং আমাদের সাথে কাজ করতে চান, তাহলে আমাদের সাথে যোগাযোগ করুন
              </p>
              <button className="px-8 py-3 bg-white text-blue-600 rounded-lg font-bold hover:bg-gray-100 transition shadow-lg">
                যোগাযোগ করুন
              </button>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default Instructors;